<template>
  <hypto-popup :title="'Create UPI Collection'">
    <template v-slot:body>
      <div class="merge-fields">
        <md-field :class="{'input-error': $v.upiId.$error || formErrors.upi_id.length}">
          <label>UPI ID</label>
          <md-input v-model="upiId" :ref="'UpiId'" @blur="$v.upiId.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.upiId.$error && !$v.upiId.required">Please enter UPI ID</h6>
        <h6 class="error-message" v-if="$v.upiId.$error && !$v.upiId.vpa">Please enter a valid UPI ID</h6>
        <error-info v-else :errorList="formErrors.upi_id" />

        <md-field :class="{'input-error': $v.amount.$error || formErrors.amount.length}">
          <label>Amount</label>
          <md-input v-model="amount" @blur="$v.amount.$touch()"
                    :type="isDesktop() ? 'text' : 'number'"
                    @keypress="allowAmountFormat($event, amount)" />
        </md-field>
        <i class="amount">{{words}}</i>
        <h6 class="error-message" v-if="$v.amount.$error && !$v.amount.amount">Please enter only numeric values (upto 2 decimals)</h6>
        <h6 class="error-message" v-else-if="$v.amount.$error && (!$v.amount.required || !$v.amount.minValue || !$v.amount.maxValue)">Amount value should be &#8377; 1 - &#8377; 1 Lakh</h6>
        <error-info v-else :errorList="formErrors.amount" />

        <md-field :class="{'input-error': $v.orderId.$error || formErrors.order_id.length}">
          <label>Order ID</label>
          <md-input v-model="orderId" @blur="$v.orderId.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.orderId.$error && !$v.orderId.required">Please enter Order ID</h6>
        <h6 class="error-message" v-if="$v.orderId.$error && !$v.orderId.alphaNum">Order ID should contain only alphanumeric characters</h6>
        <error-info v-else :errorList="formErrors.order_id" />

        <md-field :class="{'input-error': $v.note.$error || formErrors.note.length}">
          <label>Note</label>
          <md-input v-model="note" @blur="$v.note.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.note.$error && !$v.note.required">Please enter a Note</h6>
        <h6 class="error-message" v-if="$v.note.$error && !$v.note.alphaNumericSpace">Note should contain only alphanumeric characters and space</h6>
        <error-info v-else :errorList="formErrors.note" />

        <date-time @updateDateTime="(val) => expiryTime = val"
                  :customDateTime="expiryTime" :label="'Expiry Time'"
                  :fieldError="formErrors.expiry_time.length > 0 || $v.expiryTime.$error"
                  @blur="$v.expiryTime.$touch()" />
        <h6 class="error-message" v-if="$v.expiryTime.$error && !$v.expiryTime.required">Please select expiry time</h6>
        <h6 class="error-message" v-if="$v.expiryTime.$error && !$v.expiryTime.validTime">Please select a time in future</h6>
        <error-info :errorList="formErrors.expiry_time" />

        <md-field>
          <label>UDF1</label>
          <md-input v-model="udf1" />
        </md-field>

        <md-field>
          <label>UDF2</label>
          <md-input v-model="udf2" />
        </md-field>

        <md-field>
          <label>UDF3</label>
          <md-input v-model="udf3" @keyup.enter="create" />
        </md-field>
      </div>
    </template>

    <template v-slot:footer>
      <error-info :errorList="formErrors.upi_collection" />
      <button class="primary-btn" @click="create">Create</button>
    </template>
  </hypto-popup>
</template>

<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import ErrorInfo from '@/components/global/ErrorInfo'
import DateTime from '@/components/global/DateTime'

import { required, alphaNum, minValue, maxValue } from 'vuelidate/lib/validators'
import { vpa, amount, alphaNumericSpace } from '@/utils/helpers/validators'
import { allowAmountFormat, checkFormErrors, openPopup } from '@/utils/helpers'
import ApiServices from '@/services/api/upiCollections'
import numToWords from '@/mixins/numToWords'
import moment from 'moment'

const validTime = (time) => {
  return moment().isBefore(time)
}

export default {
  name: 'CreateCollection',
  components: { HyptoPopup, ErrorInfo, DateTime },
  mixins: [numToWords],
  data () {
    return {
      upiId: null,
      orderId: null,
      note: null,
      expiryTime: moment().add(15, 'm').toDate(),
      udf1: null,
      udf2: null,
      udf3: null,
      formErrors: {
        upi_id: [],
        amount: [],
        order_id: [],
        note: [],
        expiry_time: [],
        upi_collection: []
      }
    }
  },
  validations: {
    upiId: {
      required,
      vpa
    },
    amount: {
      required,
      amount,
      minValue: minValue(1),
      maxValue: maxValue(100000)
    },
    orderId: {
      required,
      alphaNum
    },
    note: {
      required,
      alphaNumericSpace
    },
    expiryTime: {
      required,
      validTime
    }
  },
  mounted () {
    this.$refs.UpiId.$el.focus()
  },
  watch: {
    upiId () {
      this.formErrors.upi_id = []
      this.formErrors.upi_collection = []
    },
    amount () {
      this.formErrors.amount = []
      this.formErrors.upi_collection = []
      this.convertWords()
    },
    orderId () {
      this.formErrors.order_id = []
      this.formErrors.upi_collection = []
    },
    note () {
      this.formErrors.note = []
      this.formErrors.upi_collection = []
    },
    expiryTime () {
      this.formErrors.expiry_time = []
      this.formErrors.upi_collection = []
    }
  },
  methods: {
    checkFormErrors,
    allowAmountFormat,
    openPopup,
    getParams () {
      return {
        upi_id: this.upiId,
        amount: parseFloat(this.amount),
        order_id: this.orderId,
        note: this.note,
        expiry_time: moment(this.expiryTime).format('YYYY-MM-DD HH:mm:ss'),
        udf1: this.udf1,
        udf2: this.udf2,
        udf3: this.udf3
      }
    },
    create () {
      this.$v.$touch()

      if (!this.$v.$error && !this.checkFormErrors(this.formErrors)) {
        const params = this.getParams()

        ApiServices.create(params).then(response => {
          if (response.success) {
            this.$emit('refreshList')
            this.successToastMsg(response.message)
          } else {
            this.errorToastMsg(response.message)
          }
        }).catch(error => {
          this.formErrors = {
            ...this.formErrors,
            ...error.body.reason
          }
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/mixins.scss';

  .amount {
    color: $grey-light;
    display: block;
    text-align: left;
    font-size: 12px;
  }
  .primary-btn {
    margin-left: auto !important;
  }
</style>
